import { Button, Tag } from '@ads/components-react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
  PencilSquareIcon,
  PlusIcon,
  TableCellsIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Spinner,
} from '@/shared/ui/atoms';
import { EmptyState } from '@/shared/ui/molecules/EmptyState';

import type { PaymentPlan, StudentPaymentSummary } from '../../../types/payment';
import { PaymentStatusBadge } from '../../atoms/PaymentStatusBadge';

export interface PaymentTableProps {
  readonly data: readonly StudentPaymentSummary[];
  readonly isLoading: boolean;
  readonly onEditPlan: (planId: number) => void;
  readonly onViewParcels: (planId: number) => void;
  readonly onCancelPlan: (planId: number) => void;
  readonly onAddPlan: (personId: number) => void;
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-quark">
      <span className="ts-caption-xxs text-ctx-content-placeholder">{label}</span>
      <span className="ts-paragraph-xs text-ctx-content-base">{value}</span>
    </div>
  );
}

function PlanCard({
  plan,
  t,
  onEdit,
  onParcels,
  onCancel,
}: {
  plan: PaymentPlan;
  t: (key: string) => string;
  onEdit: () => void;
  onParcels: () => void;
  onCancel: () => void;
}) {
  const isPlanType = plan.planType === 'renegotiation';

  return (
    <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs shadow-level1 transition-shadow hover:shadow-level2">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-xs pb-xs">
        <h4 className="ts-paragraph-xs !font-medium text-ctx-content-title">{plan.courseName}</h4>
        <PaymentStatusBadge status={plan.status} />
        {isPlanType && (
          <Tag
            label={t('planType.renegotiation')}
            size="sm"
            status="helper"
            custom={{ text: 'text-nowrap' }}
          />
        )}
        <Tag
          label={t(`paymentMethod.${plan.paymentMethod}` as never)}
          size="sm"
          status="default"
          custom={{ text: 'text-nowrap' }}
        />
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-2 gap-xs border-t-hairline border-ctx-layout-border pt-xs sm:grid-cols-3 lg:grid-cols-4">
        <InfoItem label={t('table.columns.description')} value={plan.description} />
        <InfoItem label={t('table.columns.polo')} value={plan.polo ?? '—'} />
        <InfoItem
          label={t('table.columns.totalValue')}
          value={`R$ ${plan.totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
        />
        <InfoItem label={t('table.columns.parcels')} value={`${plan.numberOfParcels}x`} />
        <InfoItem
          label={t('table.columns.creationDate')}
          value={new Date(plan.createdAt).toLocaleDateString('pt-BR')}
        />
        <InfoItem
          label={t('table.columns.enrollmentStatus')}
          value={plan.enrollmentStatus ?? '—'}
        />
        {plan.scholarshipStatus !== 'none' && (
          <InfoItem
            label={t('table.columns.scholarshipStatus')}
            value={t(`scholarshipStatus.${plan.scholarshipStatus}` as never)}
          />
        )}
        {plan.observation && (
          <InfoItem label={t('table.columns.observation')} value={plan.observation} />
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-xs border-t-hairline border-ctx-layout-border pt-xs mt-xs">
        <Button hierarchy="secondary" size="md" leadingIcon={PencilSquareIcon} onClick={onEdit}>
          {t('actions.editPlan')}
        </Button>
        <Button hierarchy="secondary" size="md" leadingIcon={TableCellsIcon} onClick={onParcels}>
          {t('actions.viewParcels')}
        </Button>
        {plan.status !== 'cancelled' && plan.status !== 'inactive' && (
          <Button hierarchy="danger" size="md" leadingIcon={XCircleIcon} onClick={onCancel}>
            {t('actions.cancelPlan')}
          </Button>
        )}
      </div>
    </div>
  );
}

export function PaymentTable({
  data,
  isLoading,
  onEditPlan,
  onViewParcels,
  onCancelPlan,
  onAddPlan,
}: PaymentTableProps) {
  const { t } = useTranslation('personManagement');
  const [openIds, setOpenIds] = useState<string[]>([]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-xl">
        <Spinner size="md" />
      </div>
    );
  }

  if (data.length === 0) {
    return <EmptyState title={t('table.empty')} />;
  }

  return (
    <Accordion type="multiple" value={openIds} onValueChange={setOpenIds}>
      <div className="flex flex-col gap-xs">
        {data.map(student => {
          const isOpen = openIds.includes(String(student.personId));
          return (
            <div
              key={student.personId}
              className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1"
            >
              <AccordionItem value={String(student.personId)}>
                <AccordionTrigger>
                  <div className="flex flex-1 items-center gap-md px-xs py-xxs">
                    {isOpen ? (
                      <ChevronDownIcon className="h-xxxs w-xxxs shrink-0 text-ctx-content-brand" />
                    ) : (
                      <ChevronRightIcon className="h-xxxs w-xxxs shrink-0 text-ctx-content-brand" />
                    )}
                    <CurrencyDollarIcon className="h-xxs w-xxs shrink-0 text-ctx-content-brand" />
                    <span className="ts-subtitle-xs font-semibold text-ctx-content-title">
                      {student.personName}
                    </span>
                    <span className="ts-caption-xxs text-ctx-content-title">
                      CPF: {student.cpf}
                    </span>
                    <span className="ts-caption-xxs text-ctx-content-title">{student.email}</span>
                    <div className="ml-auto">
                      <Tag
                        label={`${student.plans.length} plano(s)`}
                        size="sm"
                        status="default"
                        custom={{ text: 'text-nowrap' }}
                      />
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col gap-xs border-t-hairline border-ctx-layout-border bg-ctx-layout-body p-xs">
                    {student.plans.map(plan => (
                      <PlanCard
                        key={plan.id}
                        plan={plan}
                        t={t as (key: string) => string}
                        onEdit={() => onEditPlan(plan.id)}
                        onParcels={() => onViewParcels(plan.id)}
                        onCancel={() => onCancelPlan(plan.id)}
                      />
                    ))}

                    {/* Add plan button */}
                    <Button
                      hierarchy="secondary"
                      size="md"
                      leadingIcon={PlusIcon}
                      onClick={() => onAddPlan(student.personId)}
                    >
                      {t('actions.addPlan')}
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </div>
          );
        })}
      </div>
    </Accordion>
  );
}
